import { IDashboardScope } from 'src/types/graphql'
import { RedirectUrls } from 'src/types/types'
import { DashboardRole, NavKey } from './navItems'
import {
  resolveDashboardNav,
  resolveNavScopes,
  scopeHostsSomething
} from './navRules'

/** The account rows carry these themselves (Mi perfil / Mis cuentas / Admin). */
const ACCOUNT_OWNED: ReadonlySet<NavKey> = new Set(['cuenta', 'admin'])

/**
 * Admins run every client's account (Jules 2026-09-27) — a 4-item tab-bar set
 * would hide Tratos and Espacios, which they use daily.
 */
const ADMIN_PANEL: ReadonlySet<NavKey> = new Set([
  'hoy',
  'cuentas',
  'rentas',
  'espacios',
  'tratos',
  'mensajes'
])

export type AccountNav = {
  role: DashboardRole
  /** The account's phone tab-bar set, minus what the account rows carry. */
  items: NavKey[]
  /** Offer "Cambiar a modo anfitrión" rather than "Publica tu espacio". */
  hostsSomething: boolean
  loading: boolean
}

/**
 * PURE — the public-page account menu (desktop avatar popover + mobile
 * drawer). Role follows the /dashboard role landing: anything hosted → host,
 * with the declared role as the tiebreak (and the only signal while the
 * context is loading or failed). Items are the dashboard's own mobile tab bar
 * for that account, so the public menu never offers a page the panel lacks
 * and a Traditional partner never sees Rentas.
 */
export function resolveAccountNav(input: {
  scopes: IDashboardScope[] | null
  loading: boolean
  failed: boolean
  isAdmin: boolean
  declaredHost: boolean
}): AccountNav {
  const scopes = input.scopes ?? []
  const hostsSomething = input.isAdmin || scopes.some(scopeHostsSomething)
  const role: DashboardRole =
    hostsSomething || input.declaredHost ? 'host' : 'rent'
  const nav = resolveDashboardNav({
    scopes: resolveNavScopes({
      scopesInView: scopes,
      loading: input.loading,
      failed: input.failed,
      role,
      platformWide: input.isAdmin
    }),
    role,
    isAdmin: input.isAdmin
  })
  return {
    role,
    items: input.isAdmin
      ? nav.desktop.filter((k) => ADMIN_PANEL.has(k))
      : nav.mobileTabs.filter((k) => !ACCOUNT_OWNED.has(k)),
    hostsSomething,
    loading: input.loading && !input.failed && !input.isAdmin
  }
}

/**
 * Literal keys only — `t()` is typed against the `nav` resources (see
 * navItems.ts → ShellLabelKey).
 */
export type AccountRowLabelKey =
  | 'ACCOUNT_MENU.PROFILE'
  | 'ACCOUNT_MENU.TEAMS'
  | 'ACCOUNT_MENU.REFERRALS'
  | 'ACCOUNT_MENU.ADMIN'
  | 'ACCOUNT_MENU.HOME'
  | 'ACCOUNT_MENU.HELP'
  | 'ACCOUNT_MENU.SWITCH_TO_RENT'
  | 'ACCOUNT_MENU.SWITCH_TO_HOST'
  | 'ACCOUNT_MENU.PUBLISH_SPACE'

export type AccountRow = {
  key: string
  labelKey: AccountRowLabelKey
  href: string
  icon: string
}

/**
 * The account rows every account menu shares. The mobile drawer already has
 * its own Inicio / Admin / Ayuda rows, so it opts out of those three.
 */
export function accountRows(input: {
  role: DashboardRole
  isAdmin: boolean
  withHome: boolean
  withAdmin: boolean
  withHelp: boolean
}): AccountRow[] {
  const isHost = input.role === 'host'
  const rows: AccountRow[] = [
    {
      key: 'profile',
      labelKey: 'ACCOUNT_MENU.PROFILE',
      href: isHost ? RedirectUrls.HostProfile : RedirectUrls.RentProfile,
      icon: 'si-user'
    }
  ]
  if (isHost) {
    rows.push({
      key: 'teams',
      labelKey: 'ACCOUNT_MENU.TEAMS',
      href: RedirectUrls.Team,
      icon: 'si-commercial'
    })
  }
  // The referral program pays hosts, not staff.
  if (isHost && !input.isAdmin) {
    rows.push({
      key: 'referrals',
      labelKey: 'ACCOUNT_MENU.REFERRALS',
      href: RedirectUrls.Referral,
      icon: 'si-badge-dollar'
    })
  }
  if (input.withAdmin && input.isAdmin) {
    rows.push({
      key: 'admin',
      labelKey: 'ACCOUNT_MENU.ADMIN',
      href: RedirectUrls.Admin,
      icon: 'si-sliders'
    })
  }
  if (input.withHome) {
    rows.push({
      key: 'home',
      labelKey: 'ACCOUNT_MENU.HOME',
      href: RedirectUrls.Home,
      icon: 'si-house'
    })
  }
  if (input.withHelp) {
    rows.push({
      key: 'help',
      labelKey: 'ACCOUNT_MENU.HELP',
      href: RedirectUrls.Help,
      icon: 'si-ask'
    })
  }
  return rows
}

/**
 * Spec §5: "Cambiar a modo anfitrión" only for someone who hosts (a team, or
 * any personal listing); everyone else is offered the wizard instead.
 * `publishHref` comes from getSubmitLocation (unverified users go to KYC first).
 */
export function switchRow(input: {
  role: DashboardRole
  hostsSomething: boolean
  publishHref: string
}): AccountRow {
  if (input.role === 'host') {
    return {
      key: 'switch',
      labelKey: 'ACCOUNT_MENU.SWITCH_TO_RENT',
      href: RedirectUrls.DashboardRent,
      icon: 'si-my-rentals'
    }
  }
  return input.hostsSomething
    ? {
        key: 'switch',
        labelKey: 'ACCOUNT_MENU.SWITCH_TO_HOST',
        href: RedirectUrls.Dashboard,
        icon: 'si-warehouse'
      }
    : {
        key: 'publish',
        labelKey: 'ACCOUNT_MENU.PUBLISH_SPACE',
        href: input.publishHref,
        icon: 'si-house-plus'
      }
}
